import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { InputHTMLAttributes, JSX, ReactNode } from "react";

/**
 * One labelled row of the sign-in card — the customer support portal's
 * `Field` + `inputCls` pair (`pages/auth/Login.tsx`), rebuilt in MUI.
 *
 * Structure is the reference's: a baseline-aligned label row with an optional
 * right-hand affordance (that is where "Forgot?" lives), the control, and an
 * error line beneath. The control is a bare `<input>` rather than a MUI
 * `TextField` because the reference's field has no floating label, no notched
 * outline and no helper-text slot — reproducing its look through TextField
 * would mean overriding away most of what TextField is.
 *
 * The `adornment` (the password-eye toggle) sits INSIDE the input's frame and
 * shares its focus ring, so the pair reads as one control. That is why the
 * frame, not the input, carries the border in that case.
 *
 * Colours: surface and text from the theme (so the field follows light/dark),
 * focus from `palette.accent` (so the portal's teal is what a keyboard user
 * sees, exactly as blue is in the reference).
 */
export function Field({
  id,
  label,
  error,
  rightLabel,
  value,
  onChange,
  adornment,
  ...rest
}: {
  id: string;
  label: string;
  /** Validation message, rendered under the control and announced with it. */
  error?: string;
  /** Optional right-hand affordance on the label row — the "Forgot?" link. */
  rightLabel?: ReactNode;
  value: string;
  onChange: (v: string) => void;
  adornment?: ReactNode;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "id" | "value" | "onChange">): JSX.Element {
  /* Shared between the two shapes below: when there is an adornment the frame
     owns the border and the input goes bare, otherwise the input owns it. */
  const frame = {
    height: 44,
    borderRadius: 1.5,
    border: 1,
    borderColor: "divider",
    bgcolor: "background.default",
    transition: "border-color 150ms, box-shadow 150ms",
  } as const;
  const focusRing = (color: string) => ({
    borderColor: color,
    boxShadow: `0 0 0 2px ${alpha(color, 0.2)}`,
  });

  const inputBase = {
    width: "100%",
    minWidth: 0,
    background: "transparent",
    border: 0,
    outline: "none",
    fontFamily: "inherit",
    fontSize: 14.5,
    letterSpacing: "-0.01em",
    color: "text.primary",
    "&::placeholder": { color: "text.disabled" },
  } as const;

  const input = (
    <Box
      component="input"
      id={id}
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? `${id}-error` : undefined}
      {...rest}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={
        adornment
          ? inputBase
          : {
              ...inputBase,
              ...frame,
              px: 1.75,
              "&:focus": (t) => focusRing(t.palette.accent[500]),
            }
      }
    />
  );

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 0.75 }}>
        <Typography
          component="label"
          htmlFor={id}
          sx={{ fontSize: 12.5, fontWeight: 500, color: "text.primary" }}
        >
          {label}
        </Typography>
        {rightLabel && <Box sx={{ fontSize: 12 }}>{rightLabel}</Box>}
      </Box>

      {adornment ? (
        <Box
          sx={{
            ...frame,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            pl: 1.75,
            pr: 0.75,
            "&:focus-within": (t) => focusRing(t.palette.accent[500]),
          }}
        >
          {input}
          {adornment}
        </Box>
      ) : (
        input
      )}

      {error && (
        <Typography
          id={`${id}-error`}
          component="p"
          sx={{ mt: 0.75, fontSize: 12, fontWeight: 500, color: "error.main" }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
}
